import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Check, Loader2 } from "lucide-react";
import { getRestaurantId } from "../utils/JwtUtil";
import { useMealAvailability } from "../hooks/useMealAvailability";

// Import the new components
import MealAvailabilityModal from "../components/availability/MealAvailabilityModal";
import ModeToggle from "../components/availability/ModeToggle";
import MealSelector from "../components/availability/MealSelector";
import DailyMealsInfo from "../components/availability/DailyMealsInfo";
import AvailabilityCalendar from "../components/availability/AvailabilityCalendar";

export default function MealAvailability() {
  const { t } = useTranslation();
  const restaurantId = getRestaurantId();
  const [modalState, setModalState] = useState({
    isOpen: false,
    selectedDate: null
  });

  const {
    selectedMeals,
    setSelectedMeals,
    currentDate,
    changes,
    viewMode,
    setViewMode,
    localAvailability,
    filteredMeals,
    dailyMeals,
    hasChanges,
    isLoading,
    isSaving,
    mealsError,
    formatDate,
    toggleAvailability,
    saveChanges,
    navigateMonth,
    clearChanges
  } = useMealAvailability(restaurantId);

  const handleDateClick = (date, availabilities) => {
    setModalState({
      isOpen: true,
      selectedDate: date
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      selectedDate: null
    });
  };

  const handleMealSelectionChange = (newSelectedMeals) => {
    setSelectedMeals(newSelectedMeals);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-lg">{t("mealAvailability.loading")}</span>
      </div>
    );
  }

  if (mealsError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Error loading meals: {mealsError.message}</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t("noRestaurant")}</p>
      </div>
    );
  }

  if (filteredMeals.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t("mealAvailability.noNonDailyMeals")}</p>
        {dailyMeals.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-900 font-medium mb-2">
              {t("mealAvailability.onlyDailyMealsAvailable")}
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {dailyMeals.map((meal) => (
                <span
                  key={meal.id}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-200 text-blue-800"
                >
                  {meal.name}
                </span>
              ))}
            </div>
            <p className="text-xs text-blue-700 mt-2">
              {t("mealAvailability.dailyMealsAlwaysAvailable")}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Calendar className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">
            {t("mealAvailability.title")}
          </h1>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Mode Toggle */}
          <ModeToggle viewMode={viewMode} onModeChange={setViewMode} />
          
          {hasChanges && viewMode === "manage" && (
            <button
              onClick={saveChanges}
              disabled={isSaving}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t("mealAvailability.saving")}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {t("mealAvailability.saveChanges")} ({changes.size})
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Daily Meals Info */}
      <DailyMealsInfo dailyMeals={dailyMeals} />

      {/* Meal Selection - Only show in manage mode */}
      {viewMode === "manage" && (
        <MealSelector
          meals={filteredMeals}
          selectedMeals={selectedMeals}
          onMealSelectionChange={handleMealSelectionChange}
          onClearChanges={clearChanges}
        />
      )}

      {/* Calendar or Empty State */}
      {viewMode === "view" || selectedMeals.length > 0 ? (
        <AvailabilityCalendar
          currentDate={currentDate}
          onNavigateMonth={navigateMonth}
          viewMode={viewMode}
          selectedMeals={selectedMeals}
          localAvailability={localAvailability}
          changes={changes}
          onToggleAvailability={toggleAvailability}
          onDateClick={handleDateClick}
          formatDate={formatDate}
        />
      ) : (
        // Empty state when in manage mode with no meals selected
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-center">
            <div className="bg-gray-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("mealAvailability.noMealsSelectedTitle")}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {t("mealAvailability.noMealsSelectedDescription")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => setViewMode("view")}
                className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              >
                <Calendar className="h-5 w-5 mr-2" />
                {t("mealAvailability.viewAvailabilityCalendar")}
              </button>
              <div className="flex items-center text-gray-500 text-sm">
                <span className="hidden sm:inline">{t("mealAvailability.orSelectMealsAbove")}</span>
                <span className="sm:hidden">{t("mealAvailability.orSelectMealsAbove")}</span>
              </div>
            </div>
            
            {/* Visual separator and hint */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-50 border border-blue-200 rounded mr-2"></div>
                  <span>{t("mealAvailability.hasAvailabilities")}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-50 border border-gray-300 rounded mr-2"></div>
                  <span>{t("mealAvailability.noAvailabilities")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for viewing meal availabilities */}
      <MealAvailabilityModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        selectedDate={modalState.selectedDate}
        restaurantId={restaurantId}
      />
    </div>
  );
}
